import { EventEmitter } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import { Observable } from 'rxjs';
import * as _ from 'typedash';
import { appSettings } from 'app/appSettings';
import * as jPalette from '../../shared/jpalette';
import { Place } from '../interfaces/place.interface';
import { Definition } from '../store/definitions/definition.interface';
import { ErrorMessage } from './error.interface';
import { first } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Quantity } from '../store/edit-florder-detail/florder-detail.interface';
import { logger } from './logger';

const integerReg = /^[-]?\d+$/;

const numericValidator: ValidatorFn = (control: AbstractControl): { [key: string]: any } => {
  return util.isNumeric(control.value) ? null : { notANumber: { value: control.value } };
};

export interface SortableContainer {
  filter: { sortField?: string; sortAscending?: boolean };
  sortChange: EventEmitter<{ sortField: string; sortAscending: boolean }>;
}

export class util {
  static BLANK = ' '; // Must be 'true' when == , so don't use '' because it's resolved to false AS IN SELECT.COMPONENT.TS !!!

  static serializeObjectToParams(obj, prefix: string = '', usePrefixes: boolean = false): string {
    const str = [];
    let p;
    let k;
    let v;
    // the splitting of arrays wasn't happening correctly, the only way an obj === 'string' is when
    // it has an
    if (typeof obj === 'string') {
      return encodeURIComponent(prefix) + '=' + encodeURIComponent(obj);
    } else {
      for (p in obj) {
        if (obj.hasOwnProperty(p)) {
          k = usePrefixes && prefix ? prefix + '.' + p : p;
          v = obj[p];
          if (v !== null && v !== undefined) {
            // detect if array
            if (Array.isArray(v)) {
              // if array then recursive call function -> in this case the obj will be a string
              for (let index = 0; index < v.length; index++) {
                const element = v[index];
                str.push(this.serializeObjectToParams(element, k, usePrefixes));
              }
            } else {
              str.push(
                v !== null && typeof v === 'object' && !(v instanceof Date) ? this.serializeObjectToParams(v, k, usePrefixes) : encodeURIComponent(k) + '=' + encodeURIComponent(v)
              );
            }
          }
        }
      }
    }

    return str.join('&');
  }

  /**
   * Returns a deep copy of the object
   */

  static deepCopy(oldObj: any, isInternal = false) {
    let newObj = oldObj;

    if (oldObj) {
      if (oldObj instanceof Date) {
        newObj = new Date();
        newObj.setTime(oldObj.getTime());
      } else if (typeof oldObj === 'object') {
        newObj = Object.prototype.toString.call(oldObj) === '[object Array]' ? [] : {};
        for (const i in oldObj) {
          newObj[i] = this.deepCopy(oldObj[i], true);
        }
      }
    }
    return newObj;
  }

  static jsonDeepCopy = <T>(o: T): T => {
    if (!o) {
      return o;
    }
    return JSON.parse(JSON.stringify(o));
  };

  static deepMerge(target, ...sources) {
    return this._deepMerge(target, 1, ...sources);
  }

  static getNumericValidator(): ValidatorFn {
    return numericValidator;
  }

  static isNumeric(val: string | number): boolean {
    return integerReg.test('' + val);
  }

  static getDefinitionName(definitions: Definition[], id: string): string {
    return definitions.find(definition => definition.id === id)?.name || '';
  }

  static toString(val: any): string | null | undefined {
    if (val === null) {
      return null;
    }
    if (val === undefined) {
      return undefined;
    }
    return '' + val;
  }

  /**
   * Create a condition
   * @param {Function|string} conditionControl  name or function of control on which to depend, control can contain a boolean or a string as value.  If string, 'dependingValuesArray' is required
   * @param {any[]|any} conditionValues   possible values for the 'depending on'-controll
   * @returns {{conditionControl: (Function|string); conditionValues: (any[]|any)}}
   */
  static createCondition(
    conditionControl: Function | string,
    conditionValues: any[] | any = [true],
    conditionValueGetter?: Function
  ): { conditionControl: Function | string; conditionValues?: any[]; conditionValueGetter?: Function } {
    if (!Array.isArray(conditionValues)) {
      conditionValues = [conditionValues]; // eslint-disable-line
    }
    return { conditionControl, conditionValues, conditionValueGetter };
  }

  /**
   * @param form form that contains the controls
   * @param targetControl name or function of control that needs this validation
   * @param conditions: array of conditions
   * @param validatorsWhenMet initial validators
   */
  static conditionalValidation(
    form: UntypedFormGroup,
    targetControl: Function | string,
    conditions: { conditionControl: Function | string; conditionValues?: any[]; conditionValueGetter?: Function }[],
    validatorsWhenMet: ValidatorFn[],
    validatorsWhenNotMet: ValidatorFn[] = []
  ) {
    const control: UntypedFormControl = this.getControl(form, targetControl);

    const conditionsAreMet = () => {
      return conditions.reduce((conditionIsMet, condition) => {
        if (conditionIsMet === false) {
          return false;
        }
        const dependingOnControl = this.getControl(form, condition.conditionControl);
        const dependingOnValue = condition.conditionValueGetter ? condition.conditionValueGetter(dependingOnControl) : dependingOnControl.value;
        const conditionValues = condition.conditionValues || [true];
        return !!conditionValues.find(v => (typeof v === 'boolean' && !!dependingOnValue) || v === dependingOnValue); // If boolean, then everything that is true-ish is ok
      }, true);
    };

    const checker = () => {
      if (conditionsAreMet()) {
        control.setValidators(validatorsWhenMet);
      } else {
        control.setValidators(validatorsWhenNotMet);
        // control.clearValidators();
      }
      control.updateValueAndValidity();
    };

    conditions.forEach(condition => {
      const dependingOnControl = this.getControl(form, condition.conditionControl);
      dependingOnControl.valueChanges.subscribe(checker);
    });
    checker();
  }

  /**
   * Helper method to find all errors on controls
   * @param form form (or formGroup) to iterate on
   * @param errors empty array to start with.  Optional
   * @returns {Array} array of errors found
   */
  static getFormValidationErrors(form: UntypedFormGroup, errors = []) {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control instanceof UntypedFormGroup) {
        this.getFormValidationErrors(control, errors);
      }
      const controlErrors: ValidationErrors = control.errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach(keyError => {
          errors.push('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        });
      }
    });
    return errors;
  }

  static formatDate(date: Date | moment.Moment | string): string {
    if (typeof date === 'string') {
      return date;
    }
    let momentDate: moment.Moment;
    if (moment.isMoment(date)) {
      momentDate = date;
    } else {
      momentDate = moment({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate() });
    }
    return momentDate.format(appSettings.DATE_FORMAT_MOMENT_REST);
  }

  static parseDate(date: string): Date {
    const dateMoment = moment(date, appSettings.DATE_FORMAT_MOMENT_REST);
    return dateMoment.toDate();
  }

  static parseStringToMoment(date: string): moment.Moment {
    const dateMoment = moment(date, appSettings.DATE_FORMAT_MOMENT_REST);
    return dateMoment;
  }

  static isMobile(): boolean {
    const { innerWidth } = window;
    return innerWidth < 760;
  }

  static isDesktop(): boolean {
    return this.deviceType() === 'desktop';
  }

  static deviceType(): string {
    if (innerWidth < 440) {
      return 'mobile';
    } else if (innerWidth < 770) {
      return 'tablet';
    } else {
      return 'desktop';
    }
  }

  static localizeDateString(dateAsString: string): string {
    try {
      const date = dateAsString && util.parseDateString(dateAsString);
      date.month = date.month - 1;
      return (date && moment(date).format(appSettings.DATE_FORMAT_MOMENT)) || dateAsString;
    } catch (e) {
      return dateAsString;
    }
  }

  static getDefaultDefinition(definitions: Definition[]): Definition {
    return definitions.find(def => def.defaultValue);
  }

  static getValueId(value: string | { id: string; name: string }, valueAsObject: boolean = false): string {
    if (!value) {
      return valueAsObject ? null : '';
    }
    if (value['id']) {
      return (value as { id: string; name: string }).id;
    }
    return value as string;
  }

  /**
   * Helper method to create a list of items that can be used in a select field.
   * @param {T[]} children                        All children to include.  Optional.  When set, selectedId, containerArray childSelector are ignored
   * @param {string} selectedId                   ID of parent that is selected  (eg. orderType).  Ignored if children is set
   * @param {any[]} containerArray                List of all possible parents (eg. list of orderTypes).  Ignored if children is set
   * @param {(any) => T[]} childSelector          Method to select the array of children that will be used in the response.  Ignored if children is set
   * @param {AbstractControl | AbstractControl[]} childControls  Child control or array of child controls that will possibly be used to set the value (all or blank) if appropriate
   * @param {boolean} defaultAll                  If current selected value is not a valid value, set to 'all' (defaultAll=true) or Select.BLANK (defaultAll=false)
   * @param {boolean} returnObject                If true, value is an object {id: string, name: string}, otherwise it's a string (id)
   * @param {Function} cascade                    When finished, execute this function (eg orderType -> setFroms -> setTos
   * @param {Function} getTitle                   Function (optional) to generate a function to be used (eg address for from/to)
   * @param {Function} setter                     Sets the result using the setter.  Sometimes it needs to be set before doing a cascade
   * @param {boolean} sort                        Alphabetically sort the list
   * @returns {{id: any; name: any; title: (Function | any)}[]}  an array of definitions (id, name, title?) to be used in a app-select form element
   */
  static setSelectElements<T>({
                                children,
                                selectedId,
                                containerArray,
                                childSelector,
                                childControls,
                                defaultAll,
                                cascade,
                                getTitle,
                                setter,
                                returnObject,
                                sort,
                                nameForLogging,
                              }: {
    selectedId?: string;
    containerArray?: any[];
    children?: T[];
    childSelector?: (any) => T[];
    childControls: AbstractControl | AbstractControl[];
    defaultAll?: boolean;
    cascade?: Function;
    getTitle?: Function;
    setter?: Function;
    returnObject?: boolean;
    sort?: boolean;
    nameForLogging?: string;
  }) {
    const temp =
      children ||
      _.compact(containerArray || [])
        .filter(element => selectedId === 'all' || (element && selectedId === element['id']))
        .map(childSelector)
        .reduce((res, array) => {
          res.push(...array);
          return res;
        }, []);
    let elements = temp.map(child => {
      return {
        id: child['id'],
        name: child['name'] || child['id'],
        title: getTitle && getTitle(child),
        hidden: !!child['hidden'],
      };
    });

    if (!Array.isArray(childControls)) {
      childControls = [childControls];
    }

    // First filter out the duplicates
    const uniqIds = _.uniq(elements.map(e => e.id));
    elements = uniqIds.map(id => elements.find(e => e.id === id));

    childControls.forEach(childControl => {
      if (!elements.find(e => e.id === util.getValueId(childControl.value, returnObject))) {
        // invalid value or no value
        if (defaultAll) {
          // If no value, default to ALL, set value to ALL
          childControl.setValue(returnObject ? { id: 'all', name: 'All' } : 'all');
        } else if (elements.length === 1) {
          // If only 1 valid element, use that value
          childControl.setValue(returnObject ? elements[0] : elements[0].id);
        } else {
          // Set empty value
          childControl.setValue(returnObject ? null : this.BLANK);
        }
      }
    });

    if (sort) {
      elements.sort(this.nameSorter());
    }
    // Set elements first, before cascade
    setter && setter(elements);
    logger.debug(`setElements(${nameForLogging}): elements: ${elements.length}`);
    cascade && cascade.apply(this);
    return elements;
  }

  static arraysContainSameStrings(array1: string[], array2: string[]): boolean {
    if (array1.length !== array2.length) {
      return false;
    }

    const sortedArray1 = array1.slice().sort();
    const sortedArray2 = array2.slice().sort();

    for (let i = 0; i < sortedArray1.length; i++) {
      if (sortedArray1[i] !== sortedArray2[i]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Returns a new object with all properties with null or undefined value removed
   * @param object source
   * @returns new object with no null or undefined properties
   */
  static removeNullOrUndefined = object => {
    return Object.keys(object).reduce((o, key) => {
      let value = object[key];
      if (value !== null && value !== undefined) {
        if (Array.isArray(value)) {
          value = value.map(v => util.removeNullOrUndefined(v));
        } else if (typeof value === 'object') {
          value = util.removeNullOrUndefined(value);
        }
        o = Object.assign({}, o, { [key]: value });
      }
      return o;
    }, {});
  };

  static parseDateString(date: string | moment.Moment) {
    if (date) {
      if (typeof date === 'string') {
        const m = /(\d{4})-(\d{2})-(\d{2})/.exec(date);
        if (m) {
          return {
            year: parseInt(m[1], 10),
            month: parseInt(m[2], 10),
            day: parseInt(m[3], 10),
          };
        }
      } else {
        return {
          year: date.year(),
          month: date.month(),
          day: date.day(),
        }
      }
    }
    return null;
  }

  static sort<T>(arr: T[], fields: string[], ascending = true) {
    return [...arr].sort((a, b) => {
      let result = 0;
      fields.forEach(field => {
        if (result === 0) {
          result = a[field] > b[field] ? 1 : a[field] < b[field] ? -1 : 0;
        }
      });
      return ascending ? result : -1 * result;
    });
  }

  static idSorter(ascending = false) {
    return (a: Definition, b: Definition) => {
      let result;
      if (!a && b) {
        result = 1;
      } else if (!b && a) {
        result = -1;
      } else if (a === b) {
        result = 0;
      } else {
        result = a.id > b.id ? 1 : a.id < b.id ? -1 : 0;
      }
      return ascending ? result : -1 * result;
    };
  }

  static nameSorter() {
    return (a: Definition, b: Definition) => {
      let result;
      if (!b && a) {
        result = -1;
      } else if (!a && b) {
        result = 1;
      } else if (a === b) {
        result = 0;
      } else if (a.id === 'all') {
        return -1;
      } else {
        result = a.name > b.name ? 1 : a.name < b.name ? -1 : 0;
      }
      return result;
    };
  }

  static sortHelper(thiz: SortableContainer, clickedSortField: string, allowReset = true, isMutable = true) {
    if (thiz.filter.sortField === clickedSortField) {
      // Switch between ascending / descending / no sorting
      if (thiz.filter.sortAscending === true) {
        if (isMutable) thiz.filter.sortAscending = false;
        thiz.sortChange.emit({ sortField: clickedSortField, sortAscending: false });
      } else if (thiz.filter.sortAscending === false && allowReset) {
        if (isMutable) thiz.filter.sortField = null;
        thiz.sortChange.emit({ sortField: null, sortAscending: true });
      } else {
        if (isMutable) thiz.filter.sortAscending = true;
        thiz.sortChange.emit({ sortField: clickedSortField, sortAscending: true });
      }
    } else {
      if (isMutable) thiz.filter.sortField = clickedSortField;
      if (isMutable) thiz.filter.sortAscending = true;
      thiz.sortChange.emit({ sortField: clickedSortField, sortAscending: true });
    }
  }

  static generateAddressString(locations: Place[], id: string, emptyTranslation: string) {
    const location = locations && locations.find(location => location.id === id);
    const address = location && location.address;
    if (address && (address.street || address.postcode || address.city || address.country)) {
      return `${address.street || ''} ${address.postcode || ''} ${address.city || ''} ${address.country || ''}`;
    }
    return emptyTranslation;
  }

  static getWeekNumber(date: { year: number; month: number; day: number }): number {
    if (!date) {
      return null;
    }
    const d: Date = new Date(date.year, date.month - 1, date.day, 0, 0, 0, 0);
    d.setDate(d.getDate() + (d.getDay() === 0 ? -3 : 4 - d.getDay()));
    return Math.round((d.getTime() - new Date(d.getFullYear(), 0, 4).getTime()) / 86400000 / 7) + 1;
  }

  static getExtension(type: string): string {
    switch (type) {
      case 'application/pdf':
        return 'pdf';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'xlsx';
      default:
        debugger;
        return 'xlsx';
    }
  }

  static async getErrorMessage(errorResponse: HttpErrorResponse): Promise<ErrorMessage> {
    if (errorResponse.error instanceof Blob) {
      try {
        const body = await errorResponse.error.text();
        const b = JSON.parse(body);
        if (b.Message) {
          return {
            message: b.Message,
            subMessages: b.SubMessages,
            errorResponse,
          };
        }
      } catch (e) {
        // continue
      }
    }

    return this.getInstantErrorMessage(errorResponse);
  }

  static getInstantErrorMessage(errorResponse: HttpErrorResponse): ErrorMessage {
    let message = errorResponse?.error?.Message || errorResponse?.error?.statusText || errorResponse?.error?.message || errorResponse?.error?.messageCode;
    let subMessages: string[] = errorResponse?.error?.SubMessages || errorResponse?.error?.subMessages || [];

    return {
      message,
      subMessages,
      errorResponse,
    };
  }

  static openBlob(blob: Blob, filename: string) {
    // @ts-ignore
    if (window.navigator.msSaveOrOpenBlob) {
      // @ts-ignore
      window.navigator.msSaveOrOpenBlob(blob, filename);
    } else {
      FileSaver.saveAs(new File([blob], filename));
    }
  }

  static flattenJson(data: any): any {
    const result = {};
    const recurse = (cur, prop) => {
      if (Object(cur) !== cur) {
        result[prop] = cur;
      } else if (Array.isArray(cur)) {
        const l = cur.length;
        for (let i = 0; i < l; i += 1) recurse(cur[i], prop + '[' + i + ']');
        if (l === 0) result[prop] = [];
      } else {
        let isEmpty = true;
        for (const p in cur) {
          isEmpty = false;
          recurse(cur[p], prop ? prop + '.' + p : p);
        }
        if (isEmpty && prop) result[prop] = {};
      }
    };
    recurse(data, '');
    return result;
  }

  static unflattenJson(data: any): any {
    if (Object(data) !== data || Array.isArray(data)) return data;
    let result = {},
      cur,
      prop,
      idx,
      last,
      temp;
    for (const p in data) {
      (cur = result), (prop = ''), (last = 0);
      do {
        idx = p.indexOf('.', last);
        temp = p.substring(last, idx !== -1 ? idx : undefined);
        cur = cur[prop] || (cur[prop] = !isNaN(parseInt(temp, 10)) ? [] : {});
        prop = temp;
        last = idx + 1;
      } while (idx >= 0);
      cur[prop] = data[p];
    }
    return result[''];
  }

  static modalWindowVisible(): boolean {
    const overlayContainers = document.getElementsByClassName('cdk-overlay-container');
    const overlayContainer = overlayContainers && overlayContainers.length === 1 && overlayContainers[0];
    const visibleChildren = !!(overlayContainer && overlayContainer.children && Array.from(overlayContainer.children).find(c => c.clientWidth > 0 && c.clientHeight > 0));
    return visibleChildren;
  }

  static generateColors(size: number): string[] {
    // const colorMap = jPalette.ColorMap.get('rainbow');
    const colorMap = new jPalette.ColorMap(Math.max(2, size), [
      new jPalette.Color(0, 103, 177, 255), // marineblauw
      new jPalette.Color(230, 240, 247, 255), // vaalgrijs
      new jPalette.Color(10, 45, 70, 255), // donkerblauw
    ]);
    const colors: string[] = colorMap.map.map(color => color.rgb());
    return colors;
    // return shuffle(colors, "randomseed"); // import { shuffle } from "shuffle-seed";
  }

  static lightOrDark(color) {
    // Variables for red, green, blue values
    let r, g, b, hsp;

    // Check the format of the color, HEX or RGB?
    if (color.match(/^rgb/)) {
      // If RGB --> store the red, green, blue values in separate variables
      color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);

      r = color[1];
      g = color[2];
      b = color[3];
    } else {
      // If hex --> Convert it to RGB: http://gist.github.com/983661
      color = +('0x' + color.slice(1).replace(color.length < 5 && /./g, '$&$&'));

      r = color >> 16;
      g = (color >> 8) & 255;
      b = color & 255;
    }

    // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
    hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

    // Using the HSP value, determine whether the color is light or dark
    if (hsp > 127.5) {
      return 'light';
    } else {
      return 'dark';
    }
  }

  /**
   * handles errors from promise too.  Will result with an object containing an error or the result.
   * Will always return something, will never throw anything
   * @param promise promise to make safe
   */
  static safe<T>(promise: Promise<T>): Promise<{ error?: any; result?: any }> {
    return promise.then(result => ({ result })).catch(error => ({ error }));
  }

  static isNil(v: any) {
    return v === undefined || v === null;
  }

  static isNilOrEmpty(v: any) {
    if (typeof v === 'string') {
      return v.trim() === '';
    }
    if (Array.isArray(v)) {
      return v.length === 0;
    }
    return v === undefined || v === null;
  }

  static isEqual(v1: any, v2: any): boolean {
    if (this.isNilOrEmpty(v1)) {
      // eslint-disable-next-line
      v1 = null;
    }
    if (this.isNilOrEmpty(v2)) {
      // eslint-disable-next-line
      v2 = null;
    }
    return v1 === v2;
  }

  /**
   * Check if a 'any' is in an array
   * @param o
   * @param arr
   */
  static objectInArray(o: any, arr: any[]) {
    return arr.indexOf(o) >= 0;
  }

  /**
   * convert any object to a number, fallback to 0
   * @param o
   */
  static toNumber(o: any): number {
    try {
      const parsed = parseInt('' + o, 10);
      return parsed || 0; // fix NaN
    } catch (e) {
      return 0;
    }
  }

  static toIntegerIfPossible(value: string | number): string | number {
    try {
      const asInteger = parseInt('' + value, 10);
      if (!isNaN(asInteger)) {
        return asInteger;
      }
    } catch (e) {
    }
    return value;
  }

  static cleanValue = val => {
    if (typeof val === 'string') {
      return val && val.trim() && val.trim() !== 'undefined' ? val.trim() : undefined;
    }
    if (typeof val === 'number') {
      return util.toNumber(val);
    }
    return val;
  };

  private static isObject(obj) {
    return obj !== null && typeof obj === 'object';
  }

  private static isPlainObject(obj) {
    return (
      this.isObject(obj) &&
      (obj.constructor === Object || // obj = {}
        obj.constructor === undefined) // obj = Object.create(null)
    );
  }

  // Added depth to analyse delhaize contract issue
  private static _deepMerge(target, depth, ...sources) {
    // Stop if no sources
    if (!sources.length) {
      return;
    }

    for (const source of sources) {
      if (Array.isArray(target)) {
        if (Array.isArray(source)) {
          // Merge based on id
          for (const elem of source) {
            if (elem.id) {
              const targetElem = target.find(e => e.id === elem.id);
              if (targetElem) {
                this._deepMerge(targetElem, depth + 1, elem);
              } else {
                target.push(this.deepCopy(elem));
              }
            } else {
              target.push(this.deepCopy(elem));
            }
          }
        } else {
          target.push(this.deepCopy(source));
        }
      } else if (this.isPlainObject(target)) {
        if (this.isPlainObject(source)) {
          for (const key of Object.keys(source)) {
            if (!target[key]) {
              target[key] = this.deepCopy(source[key]);
            } else if (source[key]) {
              this._deepMerge(target[key], depth + 1, source[key]);
            }
          }
        } else {
          throw new Error(`Cannot merge object with non-object`);
        }
      } else {
        target = source;
      }
    }
  }

  private static getControl(form: UntypedFormGroup, nameOrFunction: Function | string) {
    if (typeof nameOrFunction === 'function') {
      return nameOrFunction(form);
    } else {
      return form.get(nameOrFunction as string);
    }
  }

  static uniq<T = any>(arr: T[], property?: string | ((o: T) => string)): T[] {
    if (!arr) {
      return arr;
    }
    const getUniqueIdentifier = (elmt: T) => {
      switch (typeof property) {
        case 'function':
          return property(elmt);
        case 'string':
          return elmt[property];
        default:
          return elmt;
      }
    };
    return arr.filter((elmt, i) => arr.findIndex(e => getUniqueIdentifier(e) === getUniqueIdentifier(elmt)) === i);
  }

  static uniqFilterFn(property?: string | ((o) => string)): (elmt, index, arr) => boolean {
    const getUniqueIdentifier = elmt => {
      switch (typeof property) {
        case 'function':
          return property(elmt);
        case 'string':
          return elmt[property];
        default:
          return elmt;
      }
    };
    return (elmt, index, arr) => {
      return arr.findIndex(e => getUniqueIdentifier(e) === getUniqueIdentifier(elmt)) === index;
    };
  }

  /**
   * using activatedRoute.snapshot.paramMap didn't work since the very first call to init is sooner than that the snapshot has queryParams.
   * @param paramName
   * @private
   */
  static getQueryParam(paramName: string): string {
    const m = new RegExp(`${paramName}=([^&]+)`, 'gi').exec(document.location.href);
    return m && decodeURIComponent(m[1]);

  }

  /**
   * a function to read a cookie value
   * https://stackoverflow.com/questions/34298133/angular-cookies
   * @param cname
   */
  static getCookie(cname) {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  static deleteCookie(name) {
    util.setCookie(name, '', -1);
  }

  static setCookie(name: string, value: string, expireDays: number, path: string = '') {
    let d: Date = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    let expires: string = `expires=${d.toUTCString()}`;
    let cpath: string = path ? `; path=${path}` : '';
    document.cookie = `${name}=${value}; ${expires}${cpath}`;
  }

}

/**
 * Returns first (latest?) value (complying with optional filter) of a live or completed observable
 * @param observable observable
 * @param filterFn optional filter to filter on
 */
export function getFirst$$<T>(observable: Observable<T>, filterFn?: (t: T) => boolean): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    observable.pipe(first()).subscribe({
      next: value => {
        resolve(value);
      },
      error: error => {
        reject(error);
      },
    });
  });
}

export function sleep$$(ms: number): Promise<void> {
  return new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });
}

export function tick$$(): Promise<void> {
  return sleep$$(0);
}

/**
 * handles errors from promise too.  Will result with an object containing an error or the result.
 * Will always return something, will never throw anything
 * @param promise promise to make safe
 */
export function safe<T>(promise: Promise<T>): Promise<{ error?: any; result?: any }> {
  return promise
    .then(result => {
      return { result };
    })
    .catch(error => {
      return { error };
    });
}

export function isHttpErrorResponse(error: any): error is HttpErrorResponse {
  return error?.name === 'HttpErrorResponse';
}

/**
 * SAP has a bug: for packings in ccrLineItems, the quantities are filled in incorrectly. packingQuantity is 0 and nrOfPallets is filled in. This function fixes that
 * Mails & telephone calls Piet Borgman february 2022
 * @param q
 */
export function fixQuantitiesForPackings(q: Quantity): Quantity {
  if (!q) {
    return q;
  }
  return {
    numberOfPallets: 0,
    numberExchangePallet: 0,
    packingQuantity: q.packingQuantity || q.numberOfPallets
  };
}

export function checkAuthorization(isAuthorized: boolean, message: string): void {
  if (!isAuthorized) {
    throw new Error(message);
  }
}
